import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { RuleBuilder } from '../../lib/components/RuleBuilder';
import { useRulesStore } from '../../stores/rulesStore';
import type { SoftRule } from '../../types';

type DialogMode = 'create' | 'edit' | null;

export const SoftRules: React.FC = () => {
  const { softRules, addSoftRule, updateSoftRule, deleteSoftRule, toggleSoftRuleActive } = useRulesStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedRule, setSelectedRule] = useState<SoftRule | null>(null);

  const handleCreate = () => {
    setSelectedRule(null);
    setDialogMode('create');
  };

  const handleEdit = (rule: SoftRule) => {
    setSelectedRule(rule);
    setDialogMode('edit');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this soft rule?')) {
      deleteSoftRule(id);
    }
  };

  const handleToggleActive = (id: string) => {
    toggleSoftRuleActive(id);
  };

  const handleSubmit = (data: SoftRule) => {
    if (dialogMode === 'create') {
      addSoftRule(data);
    } else if (dialogMode === 'edit' && selectedRule) {
      updateSoftRule(selectedRule.id, data);
    }
    setDialogMode(null);
    setSelectedRule(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedRule(null);
  };

  const getCategoryColor = (category: SoftRule['category']) => {
    const colors: Record<SoftRule['category'], 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'> = {
      teacher: 'primary',
      room: 'secondary',
      student: 'success',
      curriculum: 'warning',
      timing: 'info',
      collaboration: 'error',
      scheduling: 'primary',
    };
    return colors[category];
  };

  const sortedRules = [...softRules].sort((a, b) => b.weight - a.weight);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Soft Rules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Preferred constraints that are followed when possible, but can be violated if necessary
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Soft Rule
          </Button>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Total Rules
            </Typography>
            <Typography variant="h3">{softRules.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Active Rules
            </Typography>
            <Typography variant="h3">{softRules.filter((r) => r.isActive).length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Average Weight
            </Typography>
            <Typography variant="h3">
              {softRules.length > 0
                ? (softRules.reduce((sum, r) => sum + r.weight, 0) / softRules.length).toFixed(1)
                : '0'}
            </Typography>
          </Paper>
        </Box>

        {/* Rules Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{rule.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {rule.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={rule.category} color={getCategoryColor(rule.category)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 30 }}>
                        {rule.weight}/10
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={rule.weight * 10}
                        sx={{ flex: 1, minWidth: 60 }}
                        color={rule.weight >= 8 ? 'error' : rule.weight >= 5 ? 'warning' : 'primary'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{rule.conditions.length} condition(s)</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.isActive ? 'Active' : 'Inactive'}
                      color={rule.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(rule.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(rule)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogMode !== null} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Create New Soft Rule' : 'Edit Soft Rule'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <RuleBuilder
                rule={selectedRule || undefined}
                ruleType="soft"
                onSubmit={handleSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SoftRules;
